import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Heart, Globe, Users, Award } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';

const HEADER_BG = 'https://images.unsplash.com/photo-1767150145058-367fb68021f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBza3lsaW5lJTIwY2l0eSUyMGJ1c2luZXNzfGVufDB8fHx8MTc3MjQ0MDI1Nnww&ixlib=rb-4.1.0&q=85';
const TEAM_IMG = 'https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjB0ZWFtd29yayUyMGRpdmVyc2UlMjBwcm9mZXNzaW9uYWxzfGVufDB8fHx8MTc3MjQ0MDI1NXww&ixlib=rb-4.1.0&q=85';
const OFFICE_IMG = 'https://images.unsplash.com/photo-1636142426623-98a21fe2fb8f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyMGdsYXNzfGVufDB8fHx8MTc3MjEwMjgzNnww&ixlib=rb-4.1.0&q=85';

const values = [
  { icon: Target, title: 'Precision', desc: 'We match the right talent to the right role with surgical precision, ensuring perfect alignment between candidate capabilities and client requirements.' },
  { icon: Eye, title: 'Transparency', desc: 'Open communication at every stage of the hiring process. Our clients and candidates always know where they stand.' },
  { icon: Heart, title: 'Integrity', desc: 'We uphold the highest ethical standards in every interaction, building lasting relationships based on trust and respect.' },
];

const highlights = [
  { icon: Globe, number: '3', label: 'Countries', desc: 'Headquartered in Canada, serving USA and UK' },
  { icon: Users, number: '200+', label: 'Team Members', desc: 'Dedicated recruiters and account managers' },
  { icon: Award, number: '12+', label: 'Years Experience', desc: 'Delivering enterprise staffing solutions' },
];

const AboutUs = () => {
  return (
    <div data-testid="about-page">
      <PageHeader caption="Company" title="About Solnity" bgImage={HEADER_BG} />

      {/* Intro Two-Column */}
      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="sol-caption mb-4">Who We Are</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                A Global IT Staffing Firm Built on Expertise
              </h2>
              <p className="text-[#525252] leading-relaxed mb-4">
                Solnity is a Canada-based IT Staffing and Talent Solutions provider supporting clients across North America and the United Kingdom. Headquartered in Scarborough, Ontario, we bridge the gap between exceptional talent and enterprise opportunity, serving as a trusted partner for organizations seeking strategic workforce solutions.
              </p>
              <p className="text-[#525252] leading-relaxed mb-8">
                Our team of experienced recruiters, account managers, and industry specialists work collaboratively to understand each client's unique requirements and deliver candidates who not only meet technical qualifications but also align with organizational culture and long-term goals.
              </p>
              <Link to="/our-process" className="sol-btn-primary text-sm">
                Our Process <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="sol-img-overlay aspect-[4/3]">
              <img src={TEAM_IMG} alt="Solnity team" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {highlights.map((h) => (
              <div key={h.label} className="bg-white p-10 border border-[#E5E5E5]">
                <h.icon className="w-6 h-6 mb-5 text-black" strokeWidth={1.5} />
                <div className="text-4xl font-bold text-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{h.number}</div>
                <div className="text-sm font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{h.label}</div>
                <p className="text-sm text-[#525252]">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="sol-img-overlay aspect-[4/3]">
              <img src={OFFICE_IMG} alt="Solnity office" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div>
              <div className="mb-10">
                <p className="sol-caption mb-4">Our Mission</p>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Empowering Organizations Through Strategic Talent Solutions
                </h2>
                <p className="text-[#525252] leading-relaxed">
                  From our Canadian headquarters, we simplify the complexities of IT talent acquisition by providing enterprise clients with access to pre-vetted, highly skilled professionals who can drive business outcomes from day one.
                </p>
              </div>
              <div>
                <p className="sol-caption mb-4">Our Vision</p>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  The Preferred Global Partner for IT Workforce Solutions
                </h2>
                <p className="text-[#525252] leading-relaxed">
                  To be the most trusted Canada-based IT staffing partner across North America and the UK, recognized for our commitment to quality, innovation, and the success of our clients and candidates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">Our Values</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              What Drives Us
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((v) => (
              <div key={v.title} className="sol-service-card bg-white">
                <v.icon className="w-6 h-6 mb-5 text-black" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{v.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="sol-cta-strip">
        <div className="sol-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Work With Us?
          </h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">
            Whether you are looking to hire top talent or explore career opportunities, we are here to help.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/hire-talent" data-testid="about-cta-hire" className="sol-btn-inverse text-sm">
              Hire Talent <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="sol-btn-inverse text-sm">
              Contact Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
